import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { Document, Page, PDFViewer, Text, View } from '@react-pdf/renderer';
import { mascaraCpfCnpj } from 'pages/cpfNota/CpfNota';
import { useEffect, useState } from 'react';
import { theme } from 'theme';
import {
    getValorAPagar,
    getValorDesconto,
    getValorTotalBrindes,
    getVTBNormalizado,
    possuiBrinde,
} from 'utils/impressao-utils';
import { currencyOf, roundHalfToEven, showOnlyDate } from 'utils/util';
import { IPdfComprovanteTemplate, TDadosPessoa } from './comprovante-nota-fiscal-outros.model';
import ComprovanteNFOutrosService from './comprovante-nota-fiscal-outros.service';
import { branco, s } from './comprovante.style';
import { agrupaFormasPagamento, FormaPagamentoComprovante } from './formas-pagamento-comprovante';
import RenderComprovanteRow from './RenderComprovanteRow';

const initialState = {
    cnpj: '' as string,
    inscricaoEstadual: '' as string,
    enderecoCompleto: '' as string,
    cidadeEstado: '' as string,
};

const PdfComprovanteTemplate = (props: IPdfComprovanteTemplate) => {
    const itensQtd = props.flowVendaTO.itens.reduce(
        (prev, current) => prev + current.quantidade,
        0,
    );
    const [stateLocal, setStateLocal] = useState(initialState);
    const comprovanteService = ComprovanteNFOutrosService();

    useEffect(() => {
        comprovanteService
            .getDadosPessoa()
            .then(({ data }: { data: TDadosPessoa }) => {
                if (data.cnpj) {
                    setStateLocal({
                        cnpj: data.cnpj,
                        inscricaoEstadual: data.inscricaoEstadual,
                        enderecoCompleto: data.enderecoCompleto,
                        cidadeEstado: data.cidadeEstado,
                    });
                }
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getFormasPamento = (): Array<FormaPagamentoComprovante> =>
        agrupaFormasPagamento(props.flowVendaTO.itensPagamento);

    const isPacelavel = (descritivo: string) => {
        return (
            descritivo === 'OUTROS' || descritivo === 'CARTÃO DE CRÉDITO' || descritivo === 'CHEQUE'
        );
    };

    const _possuiBrinde = () => possuiBrinde(props.flowVendaTO.itens);
    const _getValorTotalBrindes = () => getValorTotalBrindes(props.flowVendaTO.itens);
    const _getVTBNormalizado = () => getVTBNormalizado(props.flowVendaTO);
    const _getValorDesconto = () => getValorDesconto(props.flowVendaTO);
    const _getValorAPagar = () => getValorAPagar(props.flowVendaTO);

    return (
        <>
            <IconButton
                sx={{
                    zIndex: 100000,
                    position: 'absolute',
                    bottom: 20,
                    right: 30,
                    backgroundColor: theme.palette.primary.main,
                    ':hover': {
                        backgroundColor: theme.palette.primary.main,
                        filter: 'grayscale(.2) brightness(.8) contrast(1.5)',
                    },
                }}
                onClick={props.onCloseComprovante}>
                <CloseIcon sx={{ color: theme.palette.background.default, fontSize: 45 }} />
            </IconButton>

            <PDFViewer
                style={s.pdfView}
                showToolbar={true}>
                <Document
                    title='comprovante-não-fiscal'
                    author={''}
                    language='pt-BR'>
                    <Page
                        size={'A7'}
                        orientation='portrait'
                        wrap={false}
                        style={s.page}>
                        <View
                            style={{ ...s.tableRow }}
                            fixed>
                            <View>
                                <View
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        borderBottom: '1px solid black',
                                        paddingBottom: '5px',
                                        paddingTop: '5px',
                                        marginHorizontal: '10px',
                                    }}>
                                    <Text
                                        id='comprovante-nao-fiscal-pessoa-descritivo'
                                        style={{ fontSize: '8px', fontWeight: 'medium' }}>
                                        {props.descritivo.substring(8)}
                                    </Text>
                                    <Text
                                        id='comprovante-nao-fiscal-pessoa-cnpj'
                                        style={{ fontSize: '8px' }}>
                                        CNPJ: {mascaraCpfCnpj(stateLocal.cnpj)}{' '}
                                    </Text>
                                    <Text
                                        id='comprovante-nao-fiscal-pessoa-inscricao-estadual'
                                        style={{ fontSize: '8px' }}>
                                        I.E {stateLocal.inscricaoEstadual}{' '}
                                    </Text>
                                    <Text
                                        id='comprovante-nao-fiscal-pessoa-endereco-completo'
                                        style={{ fontSize: '8px' }}>
                                        {stateLocal.enderecoCompleto}
                                    </Text>
                                    <Text
                                        id='comprovante-nao-fiscal-pessoa-cidade-estado'
                                        style={{ fontSize: '8px' }}>
                                        {stateLocal.cidadeEstado}
                                    </Text>
                                </View>
                                <View
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        borderBottom: '1px solid black',
                                        paddingBottom: '5px',
                                        paddingTop: '5px',
                                        marginHorizontal: '10px',
                                    }}>
                                    <Text
                                        id='comprovante-nao-fiscal-info-0'
                                        style={{ fontSize: '8px' }}>
                                        CÓPIA DE PEDIDO
                                    </Text>
                                    <Text
                                        id='comprovante-nao-fiscal-pessoa-info-1'
                                        style={{ fontSize: '8px' }}>
                                        DOCUMENTO NÃO FISCAL
                                    </Text>
                                </View>
                                <View style={{ paddingHorizontal: '10px' }}>
                                    <View
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            flexDirection: 'row',
                                            paddingTop: '5px',
                                        }}>
                                        <Text
                                            id='comprovante-nao-fiscal-header-produto'
                                            style={{ fontSize: '8px', fontWeight: 'bold' }}>
                                            PRODUTO
                                        </Text>
                                        <Text
                                            id='comprovante-nao-fiscal-header-descricao'
                                            style={{ fontSize: '8px', fontWeight: 'bold' }}>
                                            DESCRIÇÃO
                                        </Text>
                                        <Text
                                            id='comprovante-nao-fiscal-header-traco-0'
                                            style={{
                                                fontSize: '8px',
                                                fontWeight: 'bold',
                                                color: branco,
                                            }}>
                                            ------
                                        </Text>
                                        <Text
                                            id='comprovante-nao-fiscal-header-traco-1'
                                            style={{
                                                fontSize: '8px',
                                                fontWeight: 'bold',
                                                color: branco,
                                            }}>
                                            -------
                                        </Text>
                                    </View>
                                    <View
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            flexDirection: 'row',
                                        }}>
                                        <Text
                                            id='comprovante-nao-fiscal-header-detalhe-qtd'
                                            style={{ fontSize: '8px', fontWeight: 'bold' }}>
                                            QTD
                                        </Text>
                                        <Text
                                            id='comprovante-nao-fiscal-header-detalhe-valor-unitario'
                                            style={{ fontSize: '8px', fontWeight: 'bold' }}>
                                            VL UNIT
                                        </Text>
                                        <Text
                                            id='comprovante-nao-fiscal-header-detalhe-descricao'
                                            style={{ fontSize: '8px', fontWeight: 'bold' }}>
                                            DESC
                                        </Text>
                                        <Text
                                            id='comprovante-nao-fiscal-header-detalhe-total'
                                            style={{ fontSize: '8px', fontWeight: 'bold' }}>
                                            TOTAL
                                        </Text>
                                    </View>
                                    <View>
                                        <Text
                                            id='comprovante-nao-fiscal-separador-0'
                                            style={{ fontSize: '8px' }}>
                                            --------------------------------------------------------------------------
                                        </Text>
                                        {props.flowVendaTO?.itens.map((e, i) => {
                                            return (
                                                <RenderComprovanteRow
                                                    key={e.uuid + i}
                                                    index={i}
                                                    elemento={e}
                                                    length={props.flowVendaTO.itens.length ?? 0}
                                                    isNew={props.showPopUp}
                                                    valorDescontoPedido={
                                                        props.flowVendaTO.pedidoDescontoAcrescimo
                                                            .valorDesconto ?? 0
                                                    }
                                                />
                                            );
                                        })}
                                        <Text
                                            id='comprovante-nao-fiscal-separador-1'
                                            style={{ fontSize: '8px' }}>
                                            --------------------------------------------------------------------------
                                        </Text>
                                        <View style={{ width: '100%' }}>
                                            <View
                                                style={{
                                                    flexDirection: 'row',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <Text style={{ fontSize: '8px' }}>
                                                    QTD TOTAL DE ITENS
                                                </Text>
                                                <Text style={{ fontSize: '8px' }}>{itensQtd}</Text>
                                            </View>
                                            <View
                                                style={{
                                                    flexDirection: 'row',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <Text style={{ fontSize: '8px' }}>VALOR TOTAL</Text>
                                                <Text style={{ fontSize: '8px' }}>
                                                    {currencyOf(_getVTBNormalizado())}
                                                </Text>
                                            </View>
                                            {_possuiBrinde() && (
                                                <View
                                                    style={{
                                                        flexDirection: 'row',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                    }}>
                                                    <Text style={{ fontSize: '8px' }}>BRINDE</Text>
                                                    <Text style={{ fontSize: '8px' }}>
                                                        {currencyOf(_getValorTotalBrindes())}
                                                    </Text>
                                                </View>
                                            )}
                                            <View
                                                style={{
                                                    flexDirection: 'row',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <Text style={{ fontSize: '8px' }}>DESCONTO</Text>
                                                <Text style={{ fontSize: '8px' }}>
                                                    {currencyOf(_getValorDesconto())}
                                                </Text>
                                            </View>

                                            <View
                                                style={{
                                                    flexDirection: 'row',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <Text style={{ fontSize: '8px' }}>DEVOLUÇÕES</Text>
                                                <Text style={{ fontSize: '8px' }}>
                                                    {currencyOf(
                                                        props.flowVendaTO.valorTotalLiquidoTroca,
                                                    )}
                                                </Text>
                                            </View>

                                            <View
                                                style={{
                                                    flexDirection: 'row',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <Text style={{ fontSize: '8px' }}>ACRÉSCIMO</Text>
                                                <Text style={{ fontSize: '8px' }}>
                                                    {currencyOf(
                                                        props.flowVendaTO.pedidoDescontoAcrescimo
                                                            .acrescimo,
                                                    )}
                                                </Text>
                                            </View>

                                            {!!props.flowVendaTO.transportador?.valorFrete && (
                                                <View
                                                    style={{
                                                        flexDirection: 'row',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                    }}>
                                                    <Text style={{ fontSize: '8px' }}>
                                                        VALOR FRETE
                                                    </Text>
                                                    <Text style={{ fontSize: '8px' }}>
                                                        {currencyOf(
                                                            props.flowVendaTO.transportador
                                                                .valorFrete,
                                                        )}
                                                    </Text>
                                                </View>
                                            )}
                                            {!!props.flowVendaTO.transportador?.valorSeguro && (
                                                <View
                                                    style={{
                                                        flexDirection: 'row',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                    }}>
                                                    <Text style={{ fontSize: '8px' }}>
                                                        VALOR SEGURO
                                                    </Text>
                                                    <Text style={{ fontSize: '8px' }}>
                                                        {currencyOf(
                                                            props.flowVendaTO.transportador
                                                                .valorSeguro,
                                                        )}
                                                    </Text>
                                                </View>
                                            )}

                                            <View
                                                style={{
                                                    flexDirection: 'row',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                }}>
                                                <Text style={{ fontSize: '8px' }}>
                                                    VALOR À PAGAR
                                                </Text>
                                                <Text style={{ fontSize: '8px' }}>
                                                    {currencyOf(
                                                        roundHalfToEven(_getValorAPagar(), 2),
                                                    )}
                                                </Text>
                                            </View>
                                        </View>
                                        <Text
                                            id='comprovante-nao-fiscal-separador-2'
                                            style={{ fontSize: '8px' }}>
                                            --------------------------------------------------------------------------
                                        </Text>
                                        <View>
                                            <Text style={{ fontSize: '8px', fontWeight: 'bold' }}>
                                                Forma de pagamento
                                            </Text>
                                            {props.flowVendaTO &&
                                                getFormasPamento().map((item, i) => {
                                                    return (
                                                        <View
                                                            key={item.descritivo + i}
                                                            id={`forma_pagamento_${item.descritivo}_${i}`}
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                flexDirection: 'row',
                                                            }}>
                                                            <Text
                                                                id={`forma_pagamento_${item.descritivo}_${i}_descritivo`}
                                                                style={{ fontSize: '8px' }}>
                                                                {item.descritivo}
                                                                <Text
                                                                    style={{
                                                                        color:
                                                                            (
                                                                                isPacelavel(
                                                                                    item.descritivo,
                                                                                )
                                                                            ) ?
                                                                                theme.palette.common
                                                                                    .black
                                                                            :   'transparent',
                                                                    }}>
                                                                    {' ' + item.parcelas + 'x'}
                                                                </Text>
                                                            </Text>
                                                            <Text
                                                                id={`forma_pagamento_${item.descritivo}_${i}_valor`}
                                                                style={{ fontSize: '8px' }}>
                                                                {currencyOf(
                                                                    roundHalfToEven(item.valor, 2),
                                                                )}
                                                            </Text>
                                                        </View>
                                                    );
                                                })}
                                        </View>
                                        <Text
                                            id='comprovante-nao-fiscal-separador-3'
                                            style={{ fontSize: '8px' }}>
                                            --------------------------------------------------------------------------
                                        </Text>
                                        <View style={{ marginTop: '5px' }}>
                                            <Text
                                                id='comprovante-nao-fiscal-pedido-cliente'
                                                style={{ fontSize: '8px' }}>
                                                Cliente:{' '}
                                                {props.flowVendaTO.cliente?.descritivo ??
                                                    'CONSUMIDOR'}{' '}
                                            </Text>
                                            <Text
                                                id='comprovante-nao-fiscal-pedido-numero-pedido'
                                                style={{ fontSize: '8px' }}>
                                                Pedido número: {props.flowVendaTO.numero}
                                            </Text>
                                            <Text
                                                id='comprovante-nao-fiscal-emissao'
                                                style={{ fontSize: '8px' }}>
                                                Emissão:{' '}
                                                {props.flowVendaTO.emissao ?
                                                    showOnlyDate(
                                                        props.flowVendaTO.emissao.toString(),
                                                    )
                                                :   ''}
                                            </Text>
                                            <Text
                                                id='comprovante-nao-fiscal-pedido-canal-venda'
                                                style={{ fontSize: '8px' }}>
                                                Canal de venda:{' '}
                                                {props.flowVendaTO.canalDeVenda?.descritivo}
                                            </Text>
                                            {!!props.flowVendaTO.observacao && (
                                                <Text
                                                    id='comprovante-nao-fiscal-pedido-observacoes'
                                                    style={{ fontSize: '8px' }}>
                                                    Observações: {props.flowVendaTO.observacao}
                                                </Text>
                                            )}
                                            <Text
                                                id='comprovante-nao-fiscal-pedido-observacoes'
                                                style={{ fontSize: '8px' }}>
                                                Endereço: Avenida Paisssandu, 718 - Andar 1, Zona 5,
                                                Maringá-PR
                                            </Text>
                                            <Text
                                                id='informacao-possui-brinde'
                                                style={{ fontSize: '8px', marginTop: '4px' }}>
                                                * Produto de brinde
                                            </Text>
                                        </View>
                                    </View>
                                </View>
                            </View>
                        </View>
                    </Page>
                </Document>
            </PDFViewer>
        </>
    );
};

export default PdfComprovanteTemplate;
